import React, { useState, useEffect } from "react";
import { Icon, Button, Input, Label, Segment, List } from "semantic-ui-react";
import DijonCard from "./DijonCard";
import axiosInstance from "../../lib/auth/axiosApi";

function DijonFood(props) {
  const [recipes, setRecipes] = useState([]);
  const [mealPlan, setMealPlan] = useState([]);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('recipes');
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchRecipes();
    fetchMealPlan();
  }, []);

  const fetchRecipes = async (searchTerm = '') => {
    setLoading(true);
    try {
      const params = searchTerm ? `?search=${searchTerm}` : '';
      const res = await axiosInstance.get(`mealie/${params}`);
      setRecipes(res.data.recipes || []);
      setError(res.data.error || null);
    } catch (e) {
      setError('Could not connect to Mealie');
    }
    setLoading(false);
  };

  const fetchMealPlan = async () => {
    try {
      const res = await axiosInstance.get('mealie/meal_plan/');
      setMealPlan(res.data.meals || []);
    } catch (e) {}
  };

  const openRecipe = async (slug) => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(`mealie/recipe/${slug}/`);
      setSelectedRecipe(res.data);
      setView('detail');
    } catch (e) {
      setError('Failed to load recipe');
    }
    setLoading(false);
  };

  const renderRecipes = () => (
    <>
      <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
        <Input
          icon="search" placeholder="Search recipes..." value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && fetchRecipes(search)}
          fluid style={{ flex: 1 }}
        />
        <Button color="yellow" size="small" onClick={() => fetchRecipes(search)}>Go</Button>
      </div>

      {error && (
        <Segment inverted style={{ background: 'rgba(0,0,0,0.3)' }}>
          <Icon name="warning" color="yellow" /> {error}
          <p style={{ color: '#aaa', fontSize: '0.85em', marginTop: '5px' }}>
            Add MEALIE_URL and MEALIE_TOKEN to your env config.
          </p>
        </Segment>
      )}

      {recipes.length === 0 && !loading && !error && (
        <div style={{ textAlign: 'center', padding: '20px', color: '#888' }}>
          <Icon name="food" size="huge" />
          <p>No recipes found.</p>
        </div>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
        {recipes.map(r => (
          <div key={r.id} onClick={() => openRecipe(r.slug)}
            style={{
              width: 'calc(50% - 5px)', background: 'rgba(255,255,255,0.1)',
              borderRadius: '6px', overflow: 'hidden', cursor: 'pointer',
              border: '1px solid rgba(255,255,255,0.1)',
            }}>
            {r.image && (
              <img src={r.image} alt={r.name}
                style={{ width: '100%', height: '80px', objectFit: 'cover' }}
                onError={(e) => { e.target.style.display = 'none'; }} />
            )}
            <div style={{ padding: '8px' }}>
              <div style={{ color: '#fff', fontWeight: 'bold', fontSize: '0.85em' }}>{r.name}</div>
              {r.total_time && (
                <div style={{ color: '#aaa', fontSize: '0.75em' }}><Icon name="clock" /> {r.total_time}</div>
              )}
              {r.tags && r.tags.length > 0 && (
                <div style={{ marginTop: '4px' }}>
                  {r.tags.slice(0, 2).map((t, i) => (
                    <Label key={i} size="mini" style={{ marginBottom: '2px' }}>{t}</Label>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </>
  );

  const renderDetail = () => {
    if (!selectedRecipe) return null;
    const r = selectedRecipe;
    return (
      <>
        <Button basic inverted size="tiny" onClick={() => setView('recipes')} style={{ marginBottom: '10px' }}>
          <Icon name="arrow left" /> Back
        </Button>
        {r.image && (
          <img src={r.image} alt={r.name}
            style={{ width: '100%', height: '150px', objectFit: 'cover', borderRadius: '6px' }}
            onError={(e) => { e.target.style.display = 'none'; }} />
        )}
        <h3 style={{ color: '#d8ac49', marginTop: '10px' }}>{r.name}</h3>
        <div style={{ display: 'flex', gap: '8px', marginBottom: '10px', flexWrap: 'wrap' }}>
          {r.prep_time && <Label size="small"><Icon name="clock" /> Prep: {r.prep_time}</Label>}
          {r.cook_time && <Label size="small"><Icon name="fire" /> Cook: {r.cook_time}</Label>}
          {r.servings && <Label size="small"><Icon name="users" /> {r.servings}</Label>}
        </div>
        {r.description && <p style={{ color: '#ccc', fontStyle: 'italic' }}>{r.description}</p>}

        <h4 style={{ color: '#d8ac49' }}>Ingredients</h4>
        <List style={{ color: '#ddd' }}>
          {r.ingredients && r.ingredients.map((ing, i) => (
            <List.Item key={i} style={{ padding: '3px 0' }}>
              {ing.title && <strong style={{ color: '#d8ac49' }}>{ing.title}: </strong>}
              {ing.text}
            </List.Item>
          ))}
        </List>

        <h4 style={{ color: '#d8ac49' }}>Instructions</h4>
        <List ordered style={{ color: '#ddd' }}>
          {r.instructions && r.instructions.map((step, i) => (
            <List.Item key={i} style={{ padding: '5px 0', lineHeight: '1.5' }}>
              {step.title && <strong style={{ color: '#d8ac49' }}>{step.title}: </strong>}
              {step.text}
            </List.Item>
          ))}
        </List>

        {r.org_url && (
          <Button basic inverted size="tiny" style={{ marginTop: '10px' }}
            onClick={() => window.open(r.org_url, '_blank')}>
            <Icon name="external" /> Original Recipe
          </Button>
        )}
      </>
    );
  };

  const renderMealPlan = () => (
    <>
      {mealPlan.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '20px', color: '#888' }}>
          <Icon name="calendar" size="huge" />
          <p>No meal plans found.</p>
        </div>
      ) : (
        mealPlan.map(m => (
          <div key={m.id} style={{
            background: 'rgba(255,255,255,0.1)', borderRadius: '6px',
            padding: '10px', marginBottom: '8px',
            cursor: m.recipe?.slug ? 'pointer' : 'default',
          }} onClick={() => m.recipe?.slug && openRecipe(m.recipe.slug)}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#d8ac49', fontWeight: 'bold' }}>{m.date}</span>
              <Label size="mini">{m.entry_type || 'meal'}</Label>
            </div>
            <div style={{ color: '#fff', marginTop: '4px' }}>
              {m.recipe?.name || m.title || 'No recipe assigned'}
            </div>
          </div>
        ))
      )}
    </>
  );

  return (
    <div id="food">
      <DijonCard
        width={'450px'}
        height={'70vh'}
        activeCards={props.activeCards}
        setActiveCards={props.setActiveCards}
        name={'food'}
        content={
          <div style={{ height: '100%', overflow: 'auto', padding: '15px', background: '#2d2d2d' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h3 style={{ color: '#d8ac49', margin: 0 }}><Icon name="food" /> Recipes</h3>
              <Button.Group size="tiny">
                <Button basic={view !== 'recipes' && view !== 'detail'} inverted={view !== 'recipes' && view !== 'detail'}
                  color={view === 'recipes' || view === 'detail' ? 'yellow' : undefined}
                  onClick={() => setView('recipes')}>Recipes</Button>
                <Button basic={view !== 'mealplan'} inverted={view !== 'mealplan'}
                  color={view === 'mealplan' ? 'yellow' : undefined}
                  onClick={() => { setView('mealplan'); fetchMealPlan(); }}>Meal Plan</Button>
              </Button.Group>
            </div>

            {loading && <Segment inverted loading style={{ minHeight: '60px', background: 'transparent' }} />}
            {!loading && view === 'recipes' && renderRecipes()}
            {!loading && view === 'detail' && renderDetail()}
            {!loading && view === 'mealplan' && renderMealPlan()}
          </div>
        }
      />
    </div>
  );
}

export default DijonFood;
